export default {
  methods: {
    async queuedModule(name) {
      const queuedModule = this.state('queuedModules').find((n) => n.name === name);
      if (!queuedModule) return null;
      await queuedModule.value;
      return { name, state: 'queued' };
    },
    async reduceModules(previousPromise, name) {
      // check the module is not already loaded or queued to load
      const queuedModule = this.state('queuedModules').find((n) => n.name === name);
      const hasModule = this.state('loadedModules').includes(name) || this.hasModule(name);

      // if the module is queued start a loop to wait until its loaded
      if (queuedModule) {
        await queuedModule.value;
        return { name, state: 'queued' };
      }

      // if the module has already loaded then exit
      if (hasModule) return { name, state: 'loaded' };

      // store promise to add to queue
      const promise = this.loadWebpackModule(previousPromise, name);

      this.commit('setQueuedValue', {
        name,
        value: promise,
      });

      return promise;
    },
    /**
     * Register a module that doesn not exist with the store
     * @param {array} modules no param is passed but the components
     * data modules array is used
     * @callback (afterModulesLoaded) will run after all modules
     * have loaded and returns all the module names
     * @callback (afterModuleLoadedOnce) will run after the single module
     * has loaded passed back the module name
     * @return null
     */
    async registerModules() {
      // no modules are required to load the exit
      if (!this.modules) return null;
      // load the items in order
      await this.modules.reduce(this.reduceModules, Promise.resolve());
      // make sure we wait for all modules to load
      // sometimes the above reduce finishes early if the last queued module loads early
      const promises = this.modules.map((n) => this.queuedModule(n));
      await Promise.all(promises);

      // call function after all modules have loaded
      // this function does not care if the module exists or not
      // passes an array of the module names back that loaded
      if (typeof this.afterModulesLoaded === 'function') {
        await this.afterModulesLoaded(this.modules);
      }

      return this.modules;
    },
    async runCallBacks(name) {
      try {
        // if a action inside the loaded store exists run that before anything else is called
        if (this.$store._actions[`ndc/${name}/afterModuleLoadedOnce`]) {
          await this.dispatch(`${name}/afterModuleLoadedOnce`, name);
        }

        // call this function to only run after the store has been loaded the first time
        // passes the module name back
        if (typeof this.afterModuleLoadedOnce === 'function') {
          await this.afterModuleLoadedOnce(name);
        }
      } finally {
        // set module as loaded
        this.commit('setValue', {
          key: 'loadedModules',
          value: [...this.state('loadedModules'), name],
        });

        // eslint-disable-next-line
        return name;
      }
    },
    async loadWebpackModule(previousPromise, name) {
      // wait for previous promise to finish
      await previousPromise;
      // import store module
      // eslint-disable-next-line
      const file = await import(/* webpackChunkName: "store-[request]" */ '../store/' + name + '/index.js');
      this.registerModule(name, file.default);
      // fire callbacks for this module
      await this.runCallBacks(name);
      return name;
    },
    addTrackingModule() {
      const module = this.state('app.context.trackingService.module');
      if (!module || (Array.isArray(module) && module.length === 0)) return;
      if (this.modules.includes(module)) return;
      this.modules.unshift(module);
    },
  },
  async mounted() {
    if (!this.modules) return;
    this.catchError = null;

    try {
      this.addTrackingModule();
      await this.registerModules(this.modules);
    } catch (error) {
      this.catchError = error;
    }
  },
};
